function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { v4 as uuidV4 } from 'uuid';
import routeHelper from '../common/classes/routeHelper';
export const FAVORITES_UNAVAILABLE_USER_MESSAGE = 'Favorites are not available for your account. ' + 'Contact your administrator if this persists.';
export class FavoritesUnavailableError extends Error {
  constructor() {
    super('Favorites preferences form is unavailable');
    this.name = 'FavoritesUnavailableError';
  }
}
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True when URL is a cabinet form path whose form id segment is a UUID v4. */
export const isCabinetFormPinUrl = url => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  const path = url.split('?')[0];
  const formMatch = path.match(/\/cabinets\/[^/]+\/([^/]+)\/([^/?]+)/);
  if (!formMatch) {
    return false;
  }
  return UUID_V4_REGEX.test(formMatch[2]);
};

// Get icon based on URL - pin for form URLs with UUID id, folder otherwise
const getFavoriteIcon = (url, defaultIcon) => {
  if (defaultIcon) return defaultIcon;
  return isCabinetFormPinUrl(url) ? 'thumbtack' : 'folder-open';
};
const resolveSavedViewRowId = (row, filterId) => {
  if (row && row.id != null && String(row.id) !== '') {
    return String(row.id);
  }
  return filterId != null ? String(filterId) : '';
};
const buildSavedViewFavoriteListId = (cabinetIdentifier, filterId) => `saved-view-${cabinetIdentifier}-${filterId}`;
const pushSavedViewFavorite = (favorites, cabinetMap, cabinetIdentifier, filterId, row) => {
  const cabinet = cabinetMap.get(cabinetIdentifier);
  if (!cabinet || !row || typeof row !== 'object') return;
  const created = row.created || row._created; // eslint-disable-line no-underscore-dangle
  const name = row.name || 'Saved view';
  const rowId = resolveSavedViewRowId(row, filterId);
  favorites.push({
    id: rowId,
    label: `${cabinet.title}: ${name}`,
    url: `/cabinets/${cabinetIdentifier}?view=saved&filterId=${filterId}`,
    icon: 'filter',
    type: 'savedView',
    created,
    cabinetIdentifier,
    filterId
  });
};

/** User form `content.savedViews`: cabinetId -> filterId -> row (see savedFormFilters). */
const convertSavedViewsToFavorites = (savedViews, cabinetDefinitions, cabinetMap) => {
  const favorites = [];
  if (!savedViews || typeof savedViews !== 'object' || Array.isArray(savedViews)) {
    return favorites;
  }
  const map = cabinetMap || new Map(cabinetDefinitions.map(cd => [cd.identifier, cd]));
  Object.keys(savedViews).forEach(cabinetIdentifier => {
    const byFilter = savedViews[cabinetIdentifier];
    if (!byFilter || typeof byFilter !== 'object') return;
    if (!map.get(cabinetIdentifier)) return;
    Object.keys(byFilter).forEach(filterId => {
      const row = byFilter[filterId];
      if (!row || typeof row !== 'object') return;
      pushSavedViewFavorite(favorites, map, cabinetIdentifier, filterId, row);
    });
  });
  return favorites;
};

/**
 * Pinned favorites on userPreferences `content.favorites`: a dynamicCollection map
 * (row id === object key === value.id). See format repo userPreferencesFavoritesCollection.
 */
export const getPinnedFavoritesFromPreferencesContent = (preferencesFormContent = {}) => {
  const raw = preferencesFormContent === null || preferencesFormContent === void 0 ? void 0 : preferencesFormContent.favorites;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(fav => fav);
  }
  return Object.values(raw).filter(fav => fav);
};

/** Normalize cabinet/hash URLs so favorite pin matching is stable across navigation. */
export const normalizeFavoritePageUrl = url => {
  if (!url || typeof url !== 'string') return '';
  let path = url.startsWith('#') ? url.slice(1) : url;
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  return path;
};
export const isPageUrlFavorited = (favorites, pageUrl) => {
  const normalizedPageUrl = normalizeFavoritePageUrl(pageUrl);
  return favorites.some(fav => fav && normalizeFavoritePageUrl(fav.url) === normalizedPageUrl);
};
export const findFavoriteByPageUrl = (favorites, pageUrl) => {
  const normalizedPageUrl = normalizeFavoritePageUrl(pageUrl);
  return favorites.find(fav => fav && normalizeFavoritePageUrl(fav.url) === normalizedPageUrl) || null;
};

/** Add/update one row: `content.favorites[rowId]` merged with existing when present. */
export const buildFavoritesUpsertContentPathValue = (content, favorite) => {
  const key = String(favorite.id);
  const raw = content === null || content === void 0 ? void 0 : content.favorites;
  let value = favorite;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const existing = raw[key];
    if (existing && typeof existing === 'object') {
      value = _objectSpread(_objectSpread({}, existing), favorite);
    }
  }
  return {
    contentPath: `content/favorites/${key}`,
    value
  };
};

/**
 * Remove one row: set `content.favorites[rowId]` to null (pruned on save like other fields).
 */
export const buildFavoriteRemoveContentPathValue = (content, pinId) => {
  const raw = content === null || content === void 0 ? void 0 : content.favorites;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      contentPath: null,
      value: null
    };
  }
  const keys = Object.keys(raw);
  const key = keys.find(k => String(k) === String(pinId));
  if (key == null) {
    return {
      contentPath: null,
      value: null
    };
  }
  return {
    contentPath: `content/favorites/${key}`,
    value: null
  };
};

// Pinned URLs from userPreferences; saved cabinet filters from User form only.
export const getAllFavorites = (userContent = {}, cabinetDefinitions = [], preferencesFormContent = {}) => {
  const manualFavorites = getPinnedFavoritesFromPreferencesContent(preferencesFormContent);
  const cabinetMap = new Map(cabinetDefinitions.map(cd => [cd.identifier, cd]));
  const savedViewFavorites = convertSavedViewsToFavorites(userContent === null || userContent === void 0 ? void 0 : userContent.savedViews, cabinetDefinitions, cabinetMap);
  const manualFavoriteItems = manualFavorites.map(pin => ({
    id: `pinned-${pin.id}`,
    title: pin.label,
    subtitle: 'Pinned',
    icon: getFavoriteIcon(pin.url, pin.icon),
    type: 'pinned',
    url: pin.url,
    pinId: pin.id,
    isSavedView: false,
    onSelect: () => {
      routeHelper.updateURL(pin.url);
    }
  }));
  const savedViewItems = savedViewFavorites.map(pin => {
    const cabinet = cabinetMap.get(pin.cabinetIdentifier);
    return {
      id: buildSavedViewFavoriteListId(pin.cabinetIdentifier, pin.filterId),
      title: pin.label,
      subtitle: `Saved view in ${(cabinet === null || cabinet === void 0 ? void 0 : cabinet.title) || pin.cabinetIdentifier}`,
      icon: getFavoriteIcon(pin.url, pin.icon || 'filter'),
      type: 'pinned',
      url: pin.url,
      pinId: pin.id,
      isSavedView: true,
      cabinetIdentifier: pin.cabinetIdentifier,
      filterId: pin.filterId,
      onSelect: () => {
        routeHelper.updateURL(pin.url);
      }
    };
  });
  return [...manualFavoriteItems, ...savedViewItems];
};

// Filter favorites based on query
export const filterFavorites = (favorites, query) => {
  if (!query.trim()) return favorites;
  const lowerQuery = query.toLowerCase();
  return favorites.filter(favorite => {
    var _favorite$subtitle;
    const titleMatch = favorite.title.toLowerCase().includes(lowerQuery);
    const subtitleMatch = (_favorite$subtitle = favorite.subtitle) === null || _favorite$subtitle === void 0 ? void 0 : _favorite$subtitle.toLowerCase().includes(lowerQuery);
    return titleMatch || subtitleMatch;
  });
};

/** New pins use UUID keys (same shape as other collection row ids); map key === `id` field. */
export const generateFavoriteId = () => uuidV4().toUpperCase();

// Scroll helper to keep an element visible inside a scroll container
/**
 * Check if a favorite can be added (no unsaved form changes)
 * @param {Object} params - Parameters for checking
 * @param {Object} params.form - Form state from Redux
 * @param {Object} params.notificationActions - Notification actions (optional)
 * @returns {boolean} True if favorite can be added, false otherwise
 */
export const canAddFavorite = ({
  form: _form = {},
  notificationActions: _notificationActions = {}
}) => {
  if (_form !== null && _form !== void 0 && _form.active && _form !== null && _form !== void 0 && _form.pendingChanges) {
    if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.info) {
      _notificationActions.info({
        headerMessage: 'Cannot Add Favorite',
        message: 'Please save the form before adding it to favorites.',
        id: 'favorite-save-required'
      });
    }
    return false;
  }
  return true;
};

/**
 * Prepare to add a favorite - checks validation and gets default title
 * @param {Object} params - Parameters
 * @param {Function} params.getPageInfo - Function to get current page info
 * @param {Object} params.form - Form state from Redux
 * @param {Object} params.notificationActions - Notification actions (optional)
 * @returns {{ canProceed: boolean, defaultTitle: string }} Object with canProceed and defaultTitle
 */
export const prepareAddFavorite = ({
  getPageInfo,
  form: _form2 = {},
  notificationActions: _notificationActions2 = {}
}) => {
  var _form2$active;
  if (!canAddFavorite({
    form: _form2,
    notificationActions: _notificationActions2
  })) {
    return {
      canProceed: false,
      defaultTitle: ''
    };
  }
  const formTitle = (_form2 === null || _form2 === void 0 || (_form2$active = _form2.active) === null || _form2$active === void 0 || (_form2$active = _form2$active.content) === null || _form2$active === void 0 ? void 0 : _form2$active.title) || null;
  const pageInfo = getPageInfo(formTitle);
  return {
    canProceed: true,
    defaultTitle: pageInfo.title
  };
};

/**
 * Add a favorite with validation - checks if form has unsaved changes
 * @param {Object} params - Parameters for adding favorite
 * @param {Object} params.persistPinnedFavorite - async (favorite) => persist pin;
 *   resolves true when saved
 * @param {Function} params.getPageInfo - Function to get current page info
 * @param {string} params.title - Title for the favorite
 * @param {Object} params.form - Form state from Redux
 * @param {Object} params.notificationActions - Notification actions (optional)
 * @returns {Promise<Object|null>} The new favorite object if successful, null if blocked
 */
export const addFavoriteWithValidation = async ({
  persistPinnedFavorite,
  getPageInfo,
  title,
  form: _form3 = {},
  notificationActions: _notificationActions3 = {}
}) => {
  var _form3$active;
  // Prevent creating favorite if form has unsaved changes
  if (!canAddFavorite({
    form: _form3,
    notificationActions: _notificationActions3
  })) {
    return null;
  }
  const pageInfo = getPageInfo((_form3 === null || _form3 === void 0 || (_form3$active = _form3.active) === null || _form3$active === void 0 || (_form3$active = _form3$active.content) === null || _form3$active === void 0 ? void 0 : _form3$active.title) || null);
  const label = (typeof title === 'string' ? title.trim() : '') || 'Current View';
  const newFavorite = {
    id: generateFavoriteId(),
    label,
    url: pageInfo.url,
    icon: pageInfo.icon,
    type: 'currentView',
    created: new Date().toISOString()
  };
  let saved;
  try {
    saved = await persistPinnedFavorite(newFavorite);
  } catch (err) {
    if (err instanceof FavoritesUnavailableError) {
      if (_notificationActions3 !== null && _notificationActions3 !== void 0 && _notificationActions3.error) {
        _notificationActions3.error({
          headerMessage: 'Failed to add favorite',
          message: FAVORITES_UNAVAILABLE_USER_MESSAGE,
          id: 'favorite-add-unavailable'
        });
      }
      return null;
    }
    throw err;
  }
  if (!saved) {
    return null;
  }
  if (_notificationActions3 !== null && _notificationActions3 !== void 0 && _notificationActions3.info) {
    _notificationActions3.info({
      headerMessage: 'Favorite Added',
      message: `"${newFavorite.label}" has been added to your favorites.`,
      id: 'favorite-added'
    });
  }
  return newFavorite;
};